import { generateMedia, MediaGenerator } from 'styled-media-query';
import reboot from 'styled-reboot';
import { createGlobalStyle } from './styled-components';

// https://www.styled-components.com/docs/api#typescript

export const GlobalStyle = createGlobalStyle`
  ${reboot()}
  
  body {
    color: ${props => props.theme.colors.typography.dark.base};
    font-weight: ${props => props.theme.typography.body.fontWeight};
    font-size: ${props => props.theme.typography.body.fontSize};
    background-color: ${props => props.theme.colors.typography.dark.base};
  }

  h1 {
    font-size: ${props => props.theme.typography.title.fontSize};
    font-weight: ${props => props.theme.typography.title.fontWeight};
  }

  h2 {
    font-size: ${props => props.theme.typography.subtitle.fontSize};
    font-weight: ${props => props.theme.typography.subtitle.fontWeight};
    line-height: ${props => props.theme.typography.subtitle.lineHeight};
  } 

  h2, p {
    margin-bottom: ${props => props.theme.spacings.base}
  }

  p {
    max-width: 35rem;
  }

  a {
    color: ${props => props.theme.colors.typography.dark.base}
    :hover, :focus {
      color: ${props => props.theme.colors.typography.dark.hover}
      text-decoration: none;
    }
  }

  input, textarea {
    border:none;
  }
`;

interface IColor {
  base: string;
  hover?: string;
  focus?: string;
}

interface IMode {
  light: IColor;
  dark: IColor;
}

interface IColors {
  primary: IColor;
  secondary: IColor;
  typography: IMode;
  border: IMode;
}

const colors: IColors = {
  border: {
    dark: {
      base: '#E9E9E9'
    },
    light: {
      base: '#FFF'
    }
  },
  primary: {
    base: '#004529',
    focus: '#003620',
    hover: '#003620'
  },
  secondary: {
    base: '#FFC040',
    focus: '#EBB13B',
    hover: '#EBB13B'
  },
  typography: {
    dark: {
      base: '#232222',
      focus: '#1A1919',
      hover: '#1A1919'
    },
    light: {
      base: '#ffffff',
      focus: '#fefefe',
      hover: '#fefefe'
    }
  }
};

interface IBox {
  height: string;
  width: string;
}

interface IBoxSizes {
  large: IBox;
  medium: IBox;
}

const boxSizes: IBoxSizes = {
  large: { height: '280px', width: '280px' },
  medium: { height: '175px', width: '175px' }
};

interface ISpacings {
  base: string;
  medium: string;
  large?: string;
  tiny?: string;
}

const spacings: ISpacings = {
  base: '1rem',
  large: '4rem',
  medium: '2rem',
  tiny: '0.5rem'
};

interface IBreakpoints {
  huge: number;
  large: number;
  medium: number;
  small: number;
}

interface IUpDownBreakpoints extends IBreakpoints {
  downHuge: number;
  downLarge: number;
  downMedium: number;
  downSmall: number;
}

const breakpoints: IBreakpoints = {
  huge: 1240,
  large: 1024,
  medium: 768,
  small: 375
};

const media = generateMedia({
  downHuge: breakpoints.huge - 1 + 'px',
  downLarge: breakpoints.large - 1 + 'px',
  downMedium: breakpoints.medium - 1 + 'px',
  downSmall: breakpoints.small - 1 + 'px',
  huge: breakpoints.huge + 'px',
  large: breakpoints.large + 'px',
  medium: breakpoints.medium + 'px',
  small: breakpoints.small + 'px'
});

interface ITypography {
  fontFamily: string;
  fontWeight: number;
  fontSize: string;
  lineHeight?: string;
  letterSpacing?: string;
}

interface ITypographys {
  title: ITypography;
  subtitle: ITypography;
  meta: ITypography;
  link: ITypography;
  body: ITypography;
  button: ITypography;
  brandTitle: ITypography;
  brandTagline: ITypography;
}

const typography: ITypographys = {
  body: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '0.875rem',
    fontWeight: 100
  },
  brandTagline: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '0.875rem',
    fontWeight: 100,
    letterSpacing: '0.25rem',
    lineHeight: '2rem'
  },
  brandTitle: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '3.125rem',
    fontWeight: 200,
    letterSpacing: '0.15rem',
    lineHeight: '3.75rem'
  },
  button: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '1.3125rem',
    fontWeight: 400
  },
  link: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '0.875rem',
    fontWeight: 400
  },
  meta: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '0.6875rem',
    fontWeight: 100
  },
  subtitle: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '1.125rem',
    fontWeight: 400,
    lineHeight: '1.375rem'
  },
  title: {
    fontFamily: 'Bahnschrift, Sans-Serif',
    fontSize: '1.3125rem',
    fontWeight: 400
  }
};

export interface IThemeInterface {
  colors: IColors;
  typography: ITypographys;
  media: MediaGenerator<{ [key in keyof IUpDownBreakpoints]: string }, any>;
  spacings: ISpacings;
  boxSizes: IBoxSizes;
}

export const DefaultTheme: IThemeInterface = {
  boxSizes,
  colors,
  media,
  spacings,
  typography
};
